import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/auth/hooks/useAuth';
import { useCompanyAccountSettings } from '../hooks/useCompanyAccountSettings';
import CompanyInfoCard from '../components/CompanyInfoCard';
import CompanyAccountInfoCard from '../components/CompanyAccountInfoCard';
import CompanyNotificationCard from '../components/CompanyNotificationCard';
import SecurityCard from '../../../shared/settings/components/SecurityCard';
import DeleteAccountCard from '../../../shared/settings/components/DeleteAccountCard';
import DevicesSidebar from '../../../shared/settings/components/DevicesSidebar';
import CompanySidebar from '../../components/CompanySidebar';

const CompanyAccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const {
    settings,
    devices,
    loading,
    error,
    editMode,
    updateCompanyInfo,
    updateEmail,
    updatePassword,
    updateNotifications,
    terminateDevice,
    terminateAllSessions,
    deleteAccount,
    setEditMode,
    clearError,
  } = useCompanyAccountSettings();

  const handleDeleteAccount = async (password: string) => {
    try {
      await deleteAccount(password);
      logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Delete account error:', error);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-[#F1F3F4] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mx-auto mb-4"></div>
          <p style={{ fontFamily: 'Manrope', fontSize: '16px', color: '#757575' }}>
            Loading settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F3F4]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <CompanySidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        isMobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className={`
        transition-all duration-300
        ${sidebarCollapsed ? 'lg:ml-[104px]' : 'lg:ml-[268px]'}
      `}>
        <div className="max-w-[1589px] mx-auto px-6 py-8">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="lg:hidden w-10 h-10 rounded-lg bg-white flex items-center justify-center hover:bg-gray-100 mb-6"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 12H21M3 6H21M3 18H21" stroke="#0B111E" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
          <div>
            <h1
              style={{
                fontFamily: 'Manrope',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '38px',
                color: '#0B111E',
              }}
            >
              Account Settings
            </h1>
            <p
              style={{
                fontFamily: 'Manrope',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '22px',
                color: '#515151',
                marginTop: '2px',
              }}
            >
              Manage your company account settings and preferences
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                className="w-80 px-3 py-3 pr-10 bg-white border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6]"
                style={{
                  fontFamily: 'Manrope',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: '#A5A5A5',
                }}
              />
              <svg
                className="absolute right-3 top-1/2 -translate-y-1/2"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                  fill="#0B111E"
                />
              </svg>
            </div>

            {/* Notification Bell */}
            <div className="relative">
              <button className="w-12 h-12 rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.89 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
                    fill="#0B111E"
                  />
                </svg>
                <span className="absolute top-1 right-1 w-5 h-5 bg-[#FF5210] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                  6
                </span>
              </button>
            </div>

            {/* User Profile */}
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-xl border border-[#CBCBCB] bg-gray-200 flex items-center justify-center">
                <span style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '16px', color: '#0B111E' }}>
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <div>
                <p style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '16px', lineHeight: '26px', color: '#0B111E' }}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#A5A5A5' }}>
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex justify-between items-center">
            <p style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#DC0000' }}>{error}</p>
            <button onClick={clearError} className="text-red-600 hover:text-red-800">
              ✕
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Left Column - Settings Cards */}
          <div className="flex-1" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Company Information & Account & Security Group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <CompanyInfoCard
                companyInfo={settings.companyInfo}
                isEditing={editMode === 'companyInfo'}
                onEdit={() => setEditMode('companyInfo')}
                onSave={updateCompanyInfo}
                onCancel={() => setEditMode(null)}
                loading={loading}
              />

              <CompanyAccountInfoCard
                accountInfo={settings.accountInfo}
                isEditing={editMode === 'account'}
                onEdit={() => setEditMode('account')}
                onSave={updateEmail}
                onCancel={() => setEditMode(null)}
                loading={loading}
              />

              <SecurityCard
                isEditing={editMode === 'security'}
                onEdit={() => setEditMode('security')}
                onSave={updatePassword}
                onCancel={() => setEditMode(null)}
                loading={loading}
              />
            </div>

            {/* Notifications */}
            <CompanyNotificationCard
              settings={settings.notificationSettings}
              onUpdate={updateNotifications}
              loading={loading}
            />

            {/* Delete Account */}
            <DeleteAccountCard onDelete={handleDeleteAccount} loading={loading} />
          </div>

          {/* Right Sidebar - Devices & Security Info */}
          <div className="w-[522px]">
            <DevicesSidebar
              devices={devices}
              onTerminateDevice={terminateDevice}
              onTerminateAllSessions={terminateAllSessions}
              loading={loading}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyAccountSettings;
